import React, { useEffect, useState } from 'react';
import * as moment from 'moment';
import ThriveSignatureRequests from 'apis/ThriveSignatureRequest/ThriveSignatureRequest';

// ── Step definitions ──────────────────────────────────────────────────────────
// Logs are matched by `step_name` (never by step_id), because Unlocked / Trial
// logs use a different step_id numbering (Acknowledgment + Billing Closure are removed).
// `match` also accepts a step_id fallback for Request Initiation (always step_id 1).
const STEP_META = {
  request: {
    title: 'Request Initiation',
    match: (i) => i.step_name == 'Initiate Request' || i.step_id == '1',
  },
  acknowledgment: {
    title: 'Acknowledgment Notification',
    match: (i) => i.step_name == 'Unenrollment Notification',
  },
  cancel: {
    title: 'Cancel Unenrollment',
    match: (i) => i.step_name == 'Cancel Unenrollment',
  },
  billing: {
    title: 'Billing Closure',
    match: (i) => i.step_name == 'Billing Closure',
  },
  addon: {
    title: 'Add-on Plan Removal',
    match: (i) => i.step_name == 'Add-on Plans Remover' || i.step_name == 'Payment Plans Remover',
  },
   
};

const CLASSIC_STEPS = ['request', 'acknowledgment', 'billing', 'addon'];
const UNLOCK_TRIAL_STEPS = ['request', 'addon'];

const DATE_FORMAT = "ddd, Do MMM 'YY - h:mm A";

const Timeline = ({ requestId, plan }) => {
  const [data, setData] = useState([]);
  const [logsLoaded, setLogsLoaded] = useState(false);
  const { getLogs } = ThriveSignatureRequests();

 const isUnlocked = plan === 'Unlocked';
const isTrial = plan === 'Trial';
const isClassic = !isUnlocked && !isTrial;

const baseSteps = isClassic
  ? CLASSIC_STEPS
  : UNLOCK_TRIAL_STEPS;

const hasCancelEnrollment = data.some(
  (i) => i.step_name === 'Cancel Unenrollment'
);

let steps = baseSteps;

if (hasCancelEnrollment) {
  if (isClassic) {
    // Classic: Request → Acknowledgment → Cancel → Billing → Add-on
    steps = [
      ...baseSteps.slice(0, 2),
      'cancel',
      ...baseSteps.slice(2),
    ];
  } else {
    // Unlocked / Trial: Request → Cancel → Add-on
    steps = [
      baseSteps[0],
      'cancel',
      ...baseSteps.slice(1),
    ];
  }
}

  useEffect(() => {
    handleGetLogs();
  }, []);

  const handleGetLogs = async () => {
    try {
      const values = {
        requestId: requestId,
        type: plan === 'Unlocked' ? 'Unlock Unenrollment' : 'Unenrollment',
      };

      const res = await getLogs(values);
      if (res) {
        setData(res.data || []);
        setLogsLoaded(true);
      }
    } catch (e) {
      console.error('Get error:', e);
    }
  };

  // ── Step state derived from logs (not from backend activeStep) ──────────────
  const logsFor = (key) => (data || []).filter(STEP_META[key].match);
  const isDone = (key) => logsFor(key).length > 0;

  const firstIncompleteIdx = steps.findIndex(k => !isDone(k));

  // Active (blue) step:
  //  - the first step without logs, and
  //  - Classic only: Add-on Plan Removal is available in parallel with Billing Closure
  //    once the Acknowledgment Notification is done.
  // const isActive = (key, idx) => {
  //   if (!logsLoaded || isDone(key)) return false;
  //   if (idx === firstIncompleteIdx) return true;
  //   return isClassic && key === 'addon' && isDone('acknowledgment');
  // };

  // const getBoxColor = (key, idx) => {
  //    if (key === 'cancel') return '#F44336';
  //   if (isDone(key)) return '#4CAF50';
  //   if (isActive(key, idx)) return '#2196F3';
  //   return '#CFD8DC';
  // };


  const isActive = (key, idx) => {
    if (!logsLoaded || isDone(key)) {
        return false;
    }

    const cancelIndex = steps.indexOf('cancel');

   
    if (cancelIndex !== -1 && idx > cancelIndex) {
        return false;
    }

    // First incomplete step is active
    if (idx === firstIncompleteIdx) {
        return true;
    }

    // Classic:
    // Add-on can be active in parallel after Acknowledgment
    return (
        isClassic &&
        key === 'addon' &&
        isDone('acknowledgment')
    );
};

const getBoxColor = (key, idx) => {
    const cancelIndex = steps.indexOf('cancel');

    // Cancel = Red
    if (key === 'cancel') {
        return '#F44336';
    }

   
    if (cancelIndex !== -1 && idx > cancelIndex) {
        return '#CFD8DC';
    }

   
    if (isDone(key)) {
        return '#4CAF50';
    }

   
    if (isActive(key, idx)) {
        return '#2196F3';
    }

    // Incomplete = Grey
    return '#CFD8DC';
};

  const styles = {
    body: {
      margin: 0,
      fontFamily: 'Arial, sans-serif',
      background: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '80vh',
    },
    timeline: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      margin: '50px auto',
      padding: '10px',
      background: '#fff',
      borderRadius: '50px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      width: 'fit-content',
      gap: '5px',
    },
    boxBase: {
      position: 'relative',
      width: '120px',
      height: '50px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: '#fff',
      fontWeight: 'bold',
      textAlign: 'center',
      borderRadius: '8px',
      cursor: 'default',
      transition: 'background-color 0.3s ease',
    },
    info: {
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '200px',
      background: '#fff',
      padding: '12px 14px',
      borderRadius: '10px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      textAlign: 'left',
      fontSize: '14px',
      lineHeight: '1.4',
      boxSizing: 'border-box',
      zIndex: 10,
    },
    infoTop: {
      bottom: '150%',
      marginBottom: '10px',
    },
    infoBottom: {
      top: '150%',
      marginTop: '12px',
    },
    infoBeforeTop: {
      content: '""',
      position: 'absolute',
      left: '50%',
      width: '2px',
      height: '30px',
      background: 'currentColor',
      transform: 'translateX(-50%)',
      top: '100%',
    },
    infoBeforeBottom: {
      content: '""',
      position: 'absolute',
      left: '50%',
      width: '2px',
      height: '30px',
      background: 'currentColor',
      transform: 'translateX(-50%)',
      bottom: '100%',
    },
    heading: {
      margin: '0 0 4px',
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
    },
    incompleteHeading: {
      margin: '0 0 4px',
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      opacity: 0.5,
    },
    timestamp: {
      margin: '0 0 10px',
      fontSize: '12px',
      color: '#888',
      whiteSpace: 'nowrap',
    },
    list: {
      margin: 0,
      paddingLeft: '18px',
      color: '#555',
      fontSize: '13px',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
    },
  };

 const renderLogs = (key, logs) =>
  logs.map((d, idx) => (
    <React.Fragment key={idx}>
      {key === 'acknowledgment' ? (
        <li>{d.notes}</li>
      ) : key === 'cancel' ? (
        <>
          {d.notes && <li>{d.notes}</li>}

          {d.value && (
            <li style={{ marginTop: '8px' }}>
              Reason: {d.value}
            </li>
          )}
        </>
      ) : (
        <li>{d.notes}</li>
      )}

      <p style={styles.timestamp}>
        {moment(d.createdAt).format(DATE_FORMAT)}
      </p>
    </React.Fragment>
  ));

  return (
    <div style={styles.body}>
      <div style={styles.timeline}>
        {steps.map((key, idx) => {
          const meta = STEP_META[key];
          const logs = logsFor(key);
          const done = logs.length > 0;
          const active = isActive(key, idx);
          const color = getBoxColor(key, idx);

          const isFirst = idx === 0;
          const isLast = idx === steps.length - 1;

          // Alternate tooltip position by VISUAL position (1st above, 2nd below, ...)
          const top = idx % 2 === 0;

          const boxStyle = {
            ...styles.boxBase,
            backgroundColor: color,
            ...(isFirst && { borderTopLeftRadius: '50px', borderBottomLeftRadius: '50px' }),
            ...(isLast && { borderTopRightRadius: '50px', borderBottomRightRadius: '50px' }),
          };

          const infoStyle = {
            ...styles.info,
            ...(top ? styles.infoTop : styles.infoBottom),
            color,
          };

          const beforeStyle = {
            ...(top ? styles.infoBeforeTop : styles.infoBeforeBottom),
            backgroundColor: color,
          };

          return (
            <div key={key} style={boxStyle}>
              {idx + 1}
              <div style={infoStyle}>
                <div style={beforeStyle} />
                <h3 style={(done || active) ? styles.heading : styles.incompleteHeading}>{meta.title}</h3>
                {done && <ul style={styles.list}>{renderLogs(key, logs)}</ul>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Timeline;